import { baseApi } from "./baseApi";
import * as API from "@utils/apiPath";
import type { ApiResponse, ValuesInterface } from "interface/stateManagement";
import { StatusInterface } from "interface/common";

export const stateManagementApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getStateManagementList: builder.query<
      { statusCode: number; data: Array<{ _id?: string; id?: string; title?: string }> },
      void
    >({
      query: () => ({
        url: API.GET_STATE_MANAGEMENT_LIST,
        method: "GET",
      }),
      providesTags: ["StateManagement"],
    }),
    getStateManagements: builder.query<
      ApiResponse,
      { [key: string]: string | number }
    >({
      query: (params) => ({
        url: API.GET_STATE_MANAGEMENT,
        method: "GET",
        params,
      }),
      providesTags: ["StateManagement"],
    }),
    createStateManagement: builder.mutation<
      ApiResponse,
      { params: ValuesInterface; onClose: () => void }
    >({
      query: ({ params }) => ({
        url: API.GET_STATE_MANAGEMENT,
        method: "POST",
        body: params,
      }),
      invalidatesTags: ["StateManagement"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          arg.onClose();
        } catch (error) {
          console.error("Failed to create state management:", error);
        }
      },
    }),
    updateStateManagement: builder.mutation<
      ApiResponse,
      { params: ValuesInterface; onClose: () => void }
    >({
      query: ({ params }) => ({
        url: API.GET_STATE_MANAGEMENT,
        method: "PUT",
        body: params,
      }),
      invalidatesTags: ["StateManagement"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          arg.onClose();
        } catch (error) {
          console.error("Failed to update state management:", error);
        }
      },
    }),
    updateStateManagementStatus: builder.mutation<ApiResponse, StatusInterface>({
      query: ({ id, ...body }) => ({
        url: `/${API.GET_STATE_MANAGEMENT}/status/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["StateManagement"],
    }),
    deleteStateManagement: builder.mutation<ApiResponse, string>({
      query: (id) => ({
        url: `/${API.GET_STATE_MANAGEMENT}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["StateManagement"],
    }),
    bulkUpdateStateManagementStatus: builder.mutation<
      ApiResponse,
      { ids: string[]; status: boolean }
    >({
      query: ({ ids, status }) => ({
        url: `/${API.GET_STATE_MANAGEMENT}/bulk-status`,
        method: "PUT",
        body: { ids, status },
      }),
      invalidatesTags: ["StateManagement"],
    }),
    bulkDeleteStateManagements: builder.mutation<ApiResponse, { ids: string[] }>({
      query: ({ ids }) => ({
        url: `/${API.GET_STATE_MANAGEMENT}/bulk-delete`,
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: ["StateManagement"],
    }),
  }),
});

export const {
  useGetStateManagementListQuery,
  useGetStateManagementsQuery,
  useCreateStateManagementMutation,
  useUpdateStateManagementMutation,
  useUpdateStateManagementStatusMutation,
  useDeleteStateManagementMutation,
  useBulkUpdateStateManagementStatusMutation,
  useBulkDeleteStateManagementsMutation,
} = stateManagementApi;
